import axios from 'axios'
import {
  Project,
  ProjectPath,
  ProjectActivity,
  AppendToPassbackKind,
} from '../channel.types'

export enum EventTypes {
  visit = 'visit',
  activity = 'activity',
  read = 'read',
  click_article_link = 'click_article_link',
  category = 'category',
  link = 'link',
  favorite = 'favorite',
  share = 'share',
  expand = 'expand',
  collapse = 'collapse',
  hotspot_emoji = 'hotspot_emoji',
}

interface BasicTrackData {
  viewerKey: string
  participantId: string | null
  projectId: number
}

interface TrackEvent {
  eventType: EventTypes
}

interface ReadEvent extends TrackEvent {
  eventType: EventTypes.read
}

interface ClickArticleLinkEvent extends TrackEvent {
  eventType: EventTypes.click_article_link
  link: string
}

interface CollapseEvent extends TrackEvent {
  eventType: EventTypes.collapse
}

interface VisitEvent extends TrackEvent {
  eventType: EventTypes.visit
}

interface LinkClickEvent extends TrackEvent {
  eventType: EventTypes.link
  linkId: number
}

interface ActivityClickBasic {
  activityId: number
  categoryId?: number
  categoryDisplayPosition?: number
  displayPosition: number
  activityDisplayStateId?: number
}

interface ActivityClickEvent extends TrackEvent, ActivityClickBasic {
  eventType: EventTypes.activity
}

interface ActivityExpandEvent extends TrackEvent, ActivityClickBasic {
  eventType: EventTypes.expand
}

interface ArticleHotSpotEmojis extends TrackEvent, ActivityClickBasic {
  eventType: EventTypes.hotspot_emoji
}

interface CategoryClickEvent extends TrackEvent {
  eventType: EventTypes.category
  categoryId: number
}

interface FavoriteButtonClick extends TrackEvent, ActivityClickBasic {
  eventType: EventTypes.favorite
  isFavorite: boolean
}

interface ShareButtonClick extends TrackEvent, ActivityClickBasic {
  eventType: EventTypes.share
}

type TrackingEvent =
  | VisitEvent
  | LinkClickEvent
  | ActivityClickEvent
  | ActivityExpandEvent
  | CategoryClickEvent
  | FavoriteButtonClick
  | ShareButtonClick
  | ReadEvent
  | ClickArticleLinkEvent
  | CollapseEvent
  | ArticleHotSpotEmojis

export default class ChannelAPI {
  public static host: string

  private store: any

  public static async getChannel(slug: string): Promise<Project> {
    const { data } = await axios.get<Project>(
      ChannelAPI.host + `/api/projects/slug/${slug}`
    )
    return data
  }

  public setStoreRef(store: any) {
    this.store = store
  }

  public async getPathDetails(
    channelId: number,
    path: ProjectPath
  ): Promise<string> {
    let activity = path.activity
    let id = activity.id
    let channelActivityId = activity.activity_id
    let displayStateId = activity.display_state_id

    let endPoint = displayStateId
      ? `/api/projects/${channelId}/project_activities/${channelActivityId}/full-text/${displayStateId}`
      : `/api/projects/${channelId}/project_activities/${id}/full-text/`

    const { data } = await axios.get(ChannelAPI.host + endPoint)
    return data
  }

  public static async getViewerKey(participantId: string | null) {
    const { data } = await axios.post<string>(
      ChannelAPI.host + '/api/projects/track/viewer',
      {
        participant_id: participantId,
      }
    )
    return data
  }

  private basicTrackData(): BasicTrackData {
    let state: any = this.store.state
    return {
      projectId: state.project.id,
      participantId: state.participant_id,
      viewerKey: state.viewer_key!,
    }
  }

  public async trackEvent(event: TrackingEvent) {
    //for passback-data.
    if (event.eventType != EventTypes.visit) {
      this.store.commit('incrementChannelActions')
    }

    //might not have a selection
    if (this.store.state.selection) {
      let activityId = this.store.state.selection.activity.activity_id + ''

      //for passback-data.
      if (event.eventType == EventTypes.activity) {
        this.store.commit('appendActivityIdToPassback', {
          activityId,
          kind: AppendToPassbackKind.ACTIVITIES_PLAYED,
        })
        this.store.commit('appendActivityIdToPassback', {
          activityId,
          kind: AppendToPassbackKind.TILES_CLICKED,
        })
      } else if (event.eventType == EventTypes.expand) {
        this.store.commit('appendActivityIdToPassback', {
          activityId,
          kind: AppendToPassbackKind.TILES_CLICKED,
        })
      }
    }

    await axios.post(ChannelAPI.host + '/api/projects/track/events', {
      ...this.basicTrackData(),
      ...event,
    })
  }

  public async getViewerData() {
    const { projectId, viewerKey } = this.basicTrackData()
    const { data } = await axios.get(
      ChannelAPI.host +
        `/api/projects/${projectId}/viewer/${viewerKey}/data/channel`
    )
    return data
  }

  public async getArticleTextAndEngagements(id, projectId, displayStateId) {
    ////https://development.lumiere.is/api/projects/1/project_activities/836/full-text/10
    let endPoint =
      ChannelAPI.host +
      `/api/projects/${projectId}/project_activities/${id}/full-text/${displayStateId}`
    let { data } = await axios.get(endPoint)
    return data
  }

  public async authenticate(path: ProjectPath) {
    let url = path.activity.url
    let endPoint = ChannelAPI.host + `/api/live/${url}/token`

    try {
      const { data } = await axios.post(endPoint, {})

      return data
    } catch (error) {
      console.error(`Couldn't get a token: ${error.message}`)
    }
  }
}

const api = new ChannelAPI()
export { api }
